import { BeforeAfterUploader } from '@/components/forms/BeforeAfterUploader';
import { ImageUploader } from '@/components/forms/ImageUploader';
import { MultiImageUploader } from '@/components/forms/MultiImageUploader';
import { US_STATES } from '@/lib/constants';
import type { BeforeAfterImage, CreateListingInput } from '@/types/listing';

interface ListingStep1Props {
  data: Partial<CreateListingInput>;
  onChange: (updates: Partial<CreateListingInput>) => void;
  entityId: string;
}

export function ListingStep1({ data, onChange, entityId }: ListingStep1Props) {
  return (
    <div className="space-y-[24px]">
      {/* Basic Information */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Basic Information</h3>

        <div className="space-y-[16px]">
          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Property Title *
            </span>
            <input
              type="text"
              value={data.propertyTitle || ''}
              onChange={(e) => onChange({ propertyTitle: e.target.value })}
              className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="e.g., Charming Colonial in Historic District"
            />
          </label>

          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Property Tagline
            </span>
            <input
              type="text"
              value={data.propertyTagline || ''}
              onChange={(e) => onChange({ propertyTagline: e.target.value })}
              className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="e.g., Your Dream Home Awaits"
            />
          </label>

          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">Address *</span>
            <input
              type="text"
              value={data.address || ''}
              onChange={(e) => onChange({ address: e.target.value })}
              className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="123 Main Street"
            />
          </label>

          <div className="grid grid-cols-4 gap-[12px]">
            <label className="col-span-2 block">
              <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">City *</span>
              <input
                type="text"
                value={data.city || ''}
                onChange={(e) => onChange({ city: e.target.value })}
                className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                placeholder="City"
              />
            </label>
            <label className="block">
              <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">State *</span>
              <select
                value={data.state || ''}
                onChange={(e) => onChange({ state: e.target.value })}
                className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] text-[#181818] focus:border-[#055135] focus:outline-none"
              >
                <option value="">Select State</option>
                {US_STATES.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                Zip Code *
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={data.zipCode || ''}
                onChange={(e) => {
                  // Only allow digits and hyphen for ZIP+4 format
                  const value = e.target.value.replace(/[^\d-]/g, '');
                  onChange({ zipCode: value });
                }}
                maxLength={10}
                pattern="\d{5}(-\d{4})?"
                className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                placeholder="12345"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Neighborhood
            </span>
            <input
              type="text"
              value={data.neighborhood || ''}
              onChange={(e) => onChange({ neighborhood: e.target.value })}
              className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="e.g., Historic District"
            />
          </label>

          <label className="flex items-center gap-[12px]">
            <input
              type="checkbox"
              checked={data.isSuggested || false}
              onChange={(e) => onChange({ isSuggested: e.target.checked })}
              className="h-[18px] w-[18px] rounded border-[#e5e5e5] text-[#055135] focus:ring-[#055135]"
            />
            <span className="text-[13px] text-[#181818]">Mark as Suggested (Featured listing)</span>
          </label>

          <label className="flex items-center gap-[12px]">
            <input
              type="checkbox"
              checked={data.isPublic !== false}
              onChange={(e) => onChange({ isPublic: e.target.checked })}
              className="h-[18px] w-[18px] rounded border-[#e5e5e5] text-[#055135] focus:ring-[#055135]"
            />
            <span className="text-[13px] text-[#181818]">Public (Show on listings page)</span>
          </label>
        </div>
      </div>

      {/* Hero Image */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Hero Image</h3>
        <ImageUploader
          value={data.heroImage}
          onChange={(heroImage) => onChange({ heroImage })}
          folder="listings/hero"
          entityId={entityId}
          label=""
          recommendedMinWidth={1400}
          recommendedMinHeight={875}
        />
      </div>

      {/* Existing/After Images */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">
          Existing/After Images
        </h3>
        <BeforeAfterUploader
          value={data.beforeAfterImages || []}
          onChange={(beforeAfterImages: BeforeAfterImage[]) => onChange({ beforeAfterImages })}
          entityId={entityId}
          label=""
          maxPairs={10}
          recommendedMinWidth={800}
          recommendedMinHeight={600}
        />
      </div>

      {/* Renderings */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">3D Renderings</h3>
        <MultiImageUploader
          value={data.renderingImages || []}
          onChange={(renderingImages) => onChange({ renderingImages })}
          folder="listings/renderings"
          entityId={entityId}
          label=""
          maxImages={10}
          recommendedMinWidth={800}
          recommendedMinHeight={600}
        />
      </div>

      {/* Floor Plans */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Floor Plans</h3>
        <MultiImageUploader
          value={data.floorPlanImages || []}
          onChange={(floorPlanImages) => onChange({ floorPlanImages })}
          folder="listings/floor-plans"
          entityId={entityId}
          label=""
          maxImages={5}
          recommendedMinWidth={800}
          recommendedMinHeight={600}
        />
      </div>
    </div>
  );
}
